import _ from 'lodash';
import { DateTime, DateTimeFormatOptions } from 'luxon';

import { FeatureFlagId } from 'admin/feature-flags/FeatureFlags';
import { isAuthorized } from 'auth/Auth';
import { MetaData, MetaDataTag } from 'common/response-editor/MetadataParser';
import {
    getTimezoneOffsetMap,
    convertCalendarDateToDateTime,
    convertDateTimeToCalendarDate,
    getFirstLinkFromMetaData,
    getTemplateUrlMetaDataFromContent,
    getTextFromMetaData,
    hasTemplateUrlInMetaData,
} from 'social/common/util/Util';
import {
    CalendarDateObject,
    GoogleFields,
    LocationUid,
    ScheduleDateObject,
    SelectedSource,
    LocationSource,
    PublishType,
    SourceName,
    SourceAvailable,
    EnabledSource,
    MentionMetaDatum,
    AgeRange,
    FacebookGeoLocation,
    FacebookTargetFields,
    PostDateMetaData,
    HashtagMetaDatum,
    PostObject,
    ViewState,
    PostObjectAndSources,
    SourceState,
} from 'social/publisher/Publisher.types';

export const getScheduleDateTimeDisplayObject = (
    scheduleDate: ScheduleDateObject,
    dateFormat?: DateTimeFormatOptions,
): { dateTimeDisplay: string; timezoneDisplay: string } => {
    const format = dateFormat || _.assign({}, DateTime.DATETIME_MED, { month: 'long' });
    let dateTimeDisplay = '';
    let timezoneDisplay = '';

    // if using location timezone, we should show local time
    if (scheduleDate.useLocationTimezone) {
        timezoneDisplay = 'LOCAL_TIME_IN_PARENTHESES';
        dateTimeDisplay = DateTime.fromObject({
            year: scheduleDate.postDate.year,
            month: scheduleDate.postDate.month,
            day: scheduleDate.postDate.dayOfMonth,
            hour: scheduleDate.postDate.hour,
            minute: scheduleDate.postDate.minute,
            second: scheduleDate.postDate.second,
        }).toLocaleString(format);
    }
    // if using one timezone globally, we should show the time in that timezone
    else {
        const timezoneObj = getTimezoneOffsetMap().filter((el) => el.offset === scheduleDate.postDate.timezone)[0];

        if (timezoneObj) {
            timezoneDisplay = timezoneObj.timezone;
            dateTimeDisplay = DateTime.fromObject({
                year: scheduleDate.postDate.year,
                month: scheduleDate.postDate.month,
                day: scheduleDate.postDate.dayOfMonth,
                hour: scheduleDate.postDate.hour,
                minute: scheduleDate.postDate.minute,
                second: scheduleDate.postDate.second,
                zone: `UTC${scheduleDate.postDate.timezone}`,
            }).toLocaleString(format);
        } else {
            // if publish now, we will be using one timezone globally, since the user can be anywhere, their timezone may not be in our list.
            // if their timezone is not in our list, show them the time without timezone
            dateTimeDisplay = DateTime.fromObject({
                year: scheduleDate.postDate.year,
                month: scheduleDate.postDate.month,
                day: scheduleDate.postDate.dayOfMonth,
                hour: scheduleDate.postDate.hour,
                minute: scheduleDate.postDate.minute,
                second: scheduleDate.postDate.second,
            }).toLocaleString(format);
        }
    }
    return { dateTimeDisplay, timezoneDisplay };
};

// Get selected source names from selectedSources
export const getSourceKeysFromSelectedSources = (selectedSources: SelectedSource): SourceName[] => {
    const sourceKeys: SourceName[] = [];

    _.forEach(selectedSources, (val, key) => {
        if (val) {
            if (key !== 'IG_STORIES' && key !== 'IG_REELS') {
                sourceKeys.push(key as SourceName);
            } else if (key === 'IG_REELS') {
                if (!sourceKeys.includes('INSTAGRAM')) {
                    sourceKeys.push('INSTAGRAM');
                }
            } else if (!isAuthorized({ featureFlags: [FeatureFlagId.SocialHidePublishAssistant] })) {
                if (!sourceKeys.includes('INSTAGRAM')) {
                    sourceKeys.push('INSTAGRAM');
                }
            }
        }
    });
    return sourceKeys;
};

export const getNumOfSelectedSources = (selectedSources: SelectedSource): number => {
    let numOfSelectedSources = 0;

    _.forEach(selectedSources, (val, key) => {
        if (val) {
            numOfSelectedSources += 1;
        }
    });
    return numOfSelectedSources;
};

// Get selected location ids from selected locations
export const getLocationIdsFromSelectedLocations = (
    selectedLocations: LocationUid[],
    tenantId: number,
    locationSources: LocationSource[],
): string[] => {
    const locationIds: string[] = [];

    // Remove locations that are not in locationSources, prevent undefined value in locationIds
    selectedLocations.forEach((lUid: LocationUid) => {
        const locationObj = _.find(locationSources, (val) => {
            return val.lUid === lUid;
        });

        if (locationObj && locationObj.lCode) {
            const locationId = `${tenantId}_${locationObj.lCode}`;

            locationIds.push(locationId);
        }
    });

    return locationIds;
};

export const getLocationCodesFromSelectedLocations = (
    selectedLocations: LocationUid[],
    locationSources: LocationSource[],
): string[] => {
    const locationCodes: string[] = [];

    // Remove locations that are not in locationSources, prevent undefined value in locationCodes
    selectedLocations.forEach((lUid: LocationUid) => {
        const locationObj = _.find(locationSources, (val) => {
            return val.lUid === lUid;
        });

        if (locationObj && locationObj.lCode) {
            locationCodes.push(locationObj.lCode);
        }
    });

    return locationCodes;
};

// Get selected google fields from google fields (remove null properties)
export const getSelectedGoogleFields = (googleFields: GoogleFields): GoogleFields => {
    const { callToAction, event, alertType } = googleFields;
    const selectedFields: GoogleFields = {};

    if (callToAction) {
        selectedFields.callToAction = callToAction;
    }
    if (event) {
        selectedFields.event = event;
    }
    if (alertType) {
        selectedFields.alertType = alertType;
    }
    return selectedFields;
};

// Get date properites on ScheduledPost object (calendarDateObject, scheduleDate, postDate) from publisher scheduleDateTIme & publish type (now or later)
export const getDatePropertiesFromScheduleDateTime = (
    scheduleDateTime: ScheduleDateObject | null | undefined,
    publishType: PublishType,
): { calendarDateObject: CalendarDateObject; scheduleDate: number; postDate: ScheduleDateObject } => {
    const now = DateTime.local();
    let dateProperties = {
        calendarDateObject: convertDateTimeToCalendarDate(now),
        scheduleDate: now.toMillis(),
        postDate: {
            postDate: convertDateTimeToCalendarDate(now),
            useLocationTimezone: false,
        },
    };

    if (
        (publishType === 'PUBLISH_LATER' || publishType === 'OPTIMAL_TIMES' || publishType === 'DRAFT') &&
        scheduleDateTime
    ) {
        const scheduleDate = convertCalendarDateToDateTime(scheduleDateTime.postDate).toMillis();

        dateProperties = { calendarDateObject: scheduleDateTime.postDate, scheduleDate, postDate: scheduleDateTime };
    }

    return dateProperties;
};
// for multi dates publish
export const getPostDateMetaDataFromScheduleDateTime = (
    scheduleDateTime: ScheduleDateObject[],
    publishType: PublishType,
): PostDateMetaData[] => {
    // publish now / default
    const now = DateTime.local();
    let postDateMetaData = [
        {
            postDate: convertDateTimeToCalendarDate(now),
            useLocationTimezone: false,
            scheduledDateMillis: now.toMillis(),
        },
    ];

    if ((publishType === 'PUBLISH_LATER' || publishType === 'DRAFT') && !_.isEmpty(scheduleDateTime)) {
        postDateMetaData = scheduleDateTime.map((el) => {
            const { postDate, useLocationTimezone } = el;

            return {
                postDate,
                useLocationTimezone,
                scheduledDateMillis: convertCalendarDateToDateTime(postDate).toMillis(),
            };
        });
    }

    return postDateMetaData;
};

export const getScheduleDateTimeFromPostDateMetaData = (postDateMetaData: PostDateMetaData[]): ScheduleDateObject[] => {
    const scheduleDateTime = postDateMetaData.map((el) => {
        const { postDate, useLocationTimezone } = el;

        return {
            postDate,
            useLocationTimezone,
        };
    });

    return scheduleDateTime;
};

export const getLuidBySource = (locationSources: LocationSource[], sourceName: SourceName): LocationUid[] => {
    return locationSources
        .filter((loc) => {
            return (
                loc.sources[sourceName] === 'VALID' ||
                loc.sources[sourceName] === 'INVALID' ||
                loc.sources[sourceName] === 0 ||
                loc.sources[sourceName] === 1
            );
        })
        .map((loc) => {
            return loc.lUid;
        });
};

export const getSourcesAvailable = (
    enabledSources: EnabledSource,
    locationSources: LocationSource[],
): SourceAvailable => {
    const isSourceAvailable = (sourceName: SourceName): boolean => {
        const isSourceEnabled = enabledSources[sourceName];

        return (
            isSourceEnabled &&
            locationSources.some((val) => {
                return (
                    val.sources[sourceName] === 'VALID' ||
                    val.sources[sourceName] === 'INVALID' ||
                    val.sources[sourceName] === 0 ||
                    val.sources[sourceName] === 1 ||
                    val.sources[sourceName] === null
                );
            })
        );
    };

    return {
        FACEBOOK: isSourceAvailable('FACEBOOK'),
        TWITTER: isSourceAvailable('TWITTER'),
        INSTAGRAM: isSourceAvailable('INSTAGRAM'),
        IG_REELS: isSourceAvailable('INSTAGRAM'),
        GOOGLE_PLACES: isSourceAvailable('GOOGLE_PLACES'),
        LINKEDIN: isSourceAvailable('LINKEDIN'),
        YOUTUBE: isSourceAvailable('YOUTUBE'),
        IG_STORIES:
            !isAuthorized({ featureFlags: [FeatureFlagId.SocialHidePublishAssistant] }) &&
            isSourceAvailable('INSTAGRAM'),
        TIKTOK: isSourceAvailable('TIKTOK'),
    };
};

export const convertPlainTextToContent = (text: string): MetaData[] => {
    return [
        {
            text,
            attributes: {},
            tags: [],
        },
    ];
};

export const convertTextWithLinkToContent = (text: string, url: string): MetaData[] => {
    let content: MetaData[] = [];
    const linkStartIndex = text.indexOf(url);

    // hyperlink link text if link text is exact match for the url, otherwise hyperlink the whole text
    if (linkStartIndex > -1) {
        content = [
            {
                text: url,
                attributes: { link: url },
                tags: ['link'],
            },
        ];
        if (linkStartIndex > 0) {
            content.unshift({
                text: text.substring(0, linkStartIndex),
                attributes: {},
                tags: [],
            });
        }
        if (linkStartIndex + url.length < text.length) {
            content.push({
                text: text.substring(linkStartIndex + url.length),
                attributes: {},
                tags: [],
            });
        }
    } else {
        content = [
            {
                text,
                attributes: { link: url },
                tags: ['link'],
            },
        ];
    }

    return content;
};

export const convertTextWithMentionToContent = (text: string, mentionData: MentionMetaDatum[]): MetaData[] => {
    const content: MetaData[] = [];
    const sortedMentionData = mentionData.sort((a, b) => a.startIndex - b.startIndex);
    let currentIndex = 0;

    sortedMentionData.forEach((el) => {
        const { startIndex, endIndex } = el;

        if (el.startIndex >= currentIndex) {
            const preMentionText = text.slice(currentIndex, startIndex);

            // preMentionText can be empty when two mentions are right next to each other
            if (preMentionText) {
                content.push({
                    text: preMentionText,
                    attributes: {},
                    tags: [],
                });
            }

            if (el.externalId) {
                content.push({
                    text: el.text,
                    attributes: { additionalInfo: { source: el.source, externalId: el.externalId, link: el.link } },
                    tags: [MetaDataTag.Mention],
                });
            } else {
                content.push({
                    text: el.text,
                    attributes: { additionalInfo: { source: el.source, link: el.link } },
                    tags: [MetaDataTag.HashTag],
                });
            }

            currentIndex = endIndex;
        }
    });

    if (currentIndex < text.length) {
        const afterMentionText = text.slice(currentIndex);

        content.push({
            text: afterMentionText,
            attributes: {},
            tags: [],
        });
    }

    return content;
};

export const convertTextWithLinkAndMentionToContent = (
    text: string,
    mentionData: MentionMetaDatum[],
    url: string,
): MetaData[] => {
    let content: MetaData[] = convertTextWithMentionToContent(text, mentionData);
    // if text contains the exact match for the link, find the match and hyperlink the match, while keeping the rest of non mention text plain text
    // if text does not contain the exact match, hyperlink all the non mention text with this link
    // this assumes no overlap for mentions and link, which should not be allowed.
    const linkTextContentIndex = _.findIndex(content, (el) => el.tags.length === 0 && el.text.indexOf(url) > -1);

    if (linkTextContentIndex > -1) {
        const linkText = content[linkTextContentIndex].text;
        const linkTextContent = convertTextWithLinkToContent(linkText, url);

        content.splice(linkTextContentIndex, 1, ...linkTextContent);
    } else {
        content = content.map((el) => {
            const newEl = { ...el };

            if (newEl.tags.length === 0) {
                newEl.tags = [MetaDataTag.Link];
                newEl.attributes = { link: url };
            }
            return newEl;
        });
    }

    return content;
};

export const convertTextToContent = (
    text: string,
    mentionData?: MentionMetaDatum[] | null,
    hashtagData?: HashtagMetaDatum[] | null,
    url?: string | null,
): MetaData[] => {
    let content = convertPlainTextToContent(text);

    // since hashtagMetadata and mentionMetadata are so similar (the only difference is that hashtags don't have externalId), I'm using the mentionData object here for both mentions and hashtags (for now, and just over here, for the purposes of converting text to MetaData). This way the changes needed to account for hashtags is minimal.
    let data = mentionData;

    if (hashtagData) {
        if (!data) {
            data = [];
        }

        _.forEach(hashtagData, (hashtagDatum) => {
            data!.push({ ...hashtagDatum });
        });
    }

    if (url && data) {
        content = convertTextWithLinkAndMentionToContent(text, data, url);
    } else if (data) {
        content = convertTextWithMentionToContent(text, data);
    } else if (url) {
        content = convertTextWithLinkToContent(text, url);
    }

    return content;
};

export const isAgeFieldSameAsDefault = (ageField: AgeRange): boolean => {
    let isSame = true;

    if (ageField.ageMin !== 13 || ageField.ageMax !== 65) {
        isSame = false;
    }
    return isSame;
};

export const isLocationFieldSameAsDefault = (locationField: FacebookGeoLocation): boolean => {
    let isSame = true;

    if (!_.isEmpty(locationField.countries) || !_.isEmpty(locationField.regions) || !_.isEmpty(locationField.cities)) {
        isSame = false;
    }
    return isSame;
};

export const isFacebookTargetFieldsSameAsDefault = (targetFields: FacebookTargetFields): boolean => {
    let isSame = true;

    _.forEach(targetFields, (v, k) => {
        if (_.isEmpty(v)) {
            return;
        }
        if (k === 'ages') {
            if (!isAgeFieldSameAsDefault(v as AgeRange)) {
                isSame = false;
            }
        } else if (k === 'geoLocations') {
            if (!isLocationFieldSameAsDefault(v as FacebookGeoLocation)) {
                isSame = false;
            }
        } else {
            isSame = false;
        }
    });

    return isSame;
};

export const getNonDefaultFacebookTargetFields = (targetFields: FacebookTargetFields): FacebookTargetFields => {
    const fields: FacebookTargetFields = _.pickBy(targetFields, (v, k) => {
        if (_.isEmpty(v)) {
            return false;
        }
        if (k === 'ages') {
            return !isAgeFieldSameAsDefault(v as AgeRange);
        }
        if (k === 'geoLocations') {
            return !isLocationFieldSameAsDefault(v as FacebookGeoLocation);
        }
        return true;
    });

    return fields;
};

export const getMentionMetaDataFromContent = (content: MetaData[]): MentionMetaDatum[] => {
    const mentionMetaData: MentionMetaDatum[] = [];
    let currentIndex = 0;

    content.forEach((el) => {
        const isMention = _.includes(el.tags, MetaDataTag.Mention);

        if (isMention) {
            mentionMetaData.push({
                source: _.get(el, 'attributes.additionalInfo.source'),
                text: el.text,
                externalId: _.get(el, 'attributes.additionalInfo.externalId'),
                link: _.get(el, 'attributes.additionalInfo.link'),
                startIndex: currentIndex,
                endIndex: currentIndex + el.text.length,
            });
        }
        currentIndex += el.text.length;
    });

    return mentionMetaData;
};

export const getHashtagMetaDataFromContent = (content: MetaData[]): HashtagMetaDatum[] => {
    const hashtagMetaData: HashtagMetaDatum[] = [];
    let currentIndex = 0;

    content.forEach((el) => {
        const isHashtag = _.includes(el.tags, MetaDataTag.HashTag);

        if (isHashtag) {
            hashtagMetaData.push({
                source: _.get(el, 'attributes.additionalInfo.source'),
                text: el.text,
                link: _.get(el, 'attributes.additionalInfo.link'),
                startIndex: currentIndex,
                endIndex: currentIndex + el.text.length,
            });
        }
        currentIndex += el.text.length;
    });

    return hashtagMetaData;
};

// Shared between create and edit post, and draft post
export const getPostListFromViewState = (viewState: ViewState): PostObjectAndSources[] => {
    const postObjectsAndSources: PostObjectAndSources[] = [];
    const { sourceStates } = viewState;

    if (!viewState.isCustomizingBySource) {
        const currentSourceState: SourceState = {
            content: viewState.content,
            media: viewState.media,
            postType: viewState.postType,
            urlMetaData: viewState.urlMetaData,
            googleFields: viewState.googleFields,
            youtubeFields: viewState.youtubeFields,
            title: viewState.title,
            isShortenedUrl: viewState.isShortenedUrl,
            hasUserChosenMediaPostType: viewState.hasUserChosenMediaPostType,
            mediaPathsToAltText: viewState.mediaPathsToAltText,
        };

        postObjectsAndSources.push({
            post: getPostObjectFromSourceState(
                currentSourceState,
                viewState.customizeSourceKey,
                viewState.selectedSources,
            ),
            sources: getSourceKeysFromSelectedSources(viewState.selectedSources),
        });

        return postObjectsAndSources;
    }

    Object.keys(viewState.selectedSources).forEach((sourceName) => {
        const sourceState = sourceStates[sourceName] || sourceStates.ALL_SOURCES;

        // create post objects for all sources except the current one because current one is probably not updated
        if (
            sourceState &&
            viewState.selectedSources[sourceName] &&
            sourceName !== viewState.customizeSourceKey &&
            sourceName !== 'ALL_SOURCES'
        ) {
            const postObject = getPostObjectFromSourceState(
                sourceState,
                sourceName as SourceName | 'ALL_SOURCES',
                viewState.selectedSources,
            );

            const existingObject = _.find(postObjectsAndSources, { post: postObject }) as
                | PostObjectAndSources
                | undefined;

            if (existingObject) {
                existingObject.sources.push(sourceName as SourceName);
            } else {
                postObjectsAndSources.push({
                    post: postObject,
                    sources:
                        sourceName === 'ALL_SOURCES'
                            ? getSourceKeysFromSelectedSources(viewState.selectedSources)
                            : [sourceName as SourceName],
                });
            }
        }
    });

    // current sourceState doesn't get saved or updated so we need to grab current viewState
    const currentSourceState: SourceState = {
        content: viewState.content,
        media: viewState.media,
        postType: viewState.postType,
        urlMetaData: viewState.urlMetaData,
        googleFields: viewState.googleFields,
        youtubeFields: viewState.youtubeFields,
        title: viewState.title,
        isShortenedUrl: viewState.isShortenedUrl,
        hasUserChosenMediaPostType: viewState.hasUserChosenMediaPostType,
        mediaPathsToAltText: viewState.mediaPathsToAltText,
    };

    const postObject = getPostObjectFromSourceState(
        currentSourceState,
        viewState.customizeSourceKey,
        viewState.selectedSources,
    );

    const existingObject = _.find(postObjectsAndSources, { post: postObject }) as PostObjectAndSources | undefined;

    if (existingObject) {
        existingObject.sources.push(viewState.customizeSourceKey as SourceName);
    } else {
        postObjectsAndSources.push({
            post: postObject,
            sources:
                viewState.customizeSourceKey === 'ALL_SOURCES'
                    ? getSourceKeysFromSelectedSources(viewState.selectedSources)
                    : [viewState.customizeSourceKey],
        });
    }

    return postObjectsAndSources;
};

export const getPostObjectFromSourceState = (
    sourceState: SourceState,
    sourceName: SourceName | 'ALL_SOURCES',
    selectedSources: SelectedSource,
): PostObject => {
    const postObject: PostObject = {
        text: '',
    };

    // Content
    if (sourceState.content && sourceState.content.length > 0) {
        postObject.text = getTextFromMetaData(sourceState.content);
    }

    // Media
    if (sourceState.media && sourceState.media.length > 0) {
        postObject.mediaPaths = sourceState.media.map((mediaObj) => {
            return mediaObj.url;
        });
    }

    // Mentions
    if (hasMentionInContent(sourceState.content)) {
        postObject.mentionMetaData = getMentionMetaDataFromContent(sourceState.content);
    }

    // Hashtags
    if (hasHashtagInContent(sourceState.content)) {
        postObject.hashtagMetaData = getHashtagMetaDataFromContent(sourceState.content);
    }

    // template url
    if (hasTemplateUrlInMetaData(sourceState.content)) {
        postObject.macroMetaData = getTemplateUrlMetaDataFromContent(sourceState.content);
    }

    // PostType
    if (sourceState.postType === 'LINK') {
        // Do not carry over any attached media for link posts
        if (postObject.mediaPaths) {
            delete postObject.mediaPaths;
        }

        if (sourceState.urlMetaData) {
            const { description, title, images } = sourceState.urlMetaData;
            const url = getFirstLinkFromMetaData(sourceState.content) as string;

            postObject.link = {
                url,
                description,
                name: title,
            };
            if (!_.isEmpty(images)) {
                postObject.mediaPaths = [images[0].url];
            }
        }
    }

    // GOOGLE_PLACES values
    if (sourceName === 'GOOGLE_PLACES' || (sourceName === 'ALL_SOURCES' && selectedSources.GOOGLE_PLACES)) {
        const googleFields = getSelectedGoogleFields(sourceState.googleFields);

        _.assign(postObject, googleFields);
    }

    // YOUTUBE values
    if (sourceName === 'YOUTUBE' || (sourceName === 'ALL_SOURCES' && selectedSources.YOUTUBE)) {
        const youtubeFields = { ...sourceState.youtubeFields };

        _.assign(postObject, { title: sourceState.title }, youtubeFields);
    }

    if (sourceName === 'IG_STORIES' || (sourceName === 'ALL_SOURCES' && selectedSources.IG_STORIES)) {
        postObject.story = true;
    }

    if (sourceName === 'IG_REELS' || (sourceName === 'ALL_SOURCES' && selectedSources.IG_REELS)) {
        postObject.reel = true;
    }

    if (sourceState.mediaPathsToAltText) {
        // backend expects string[][] because mongo doesn't allow '.' in map keys
        const mediaAltText: string[][] = [];

        Object.keys(sourceState.mediaPathsToAltText).forEach((key) => {
            mediaAltText.push([key, sourceState.mediaPathsToAltText![key]]);
        });

        postObject.mediaPathsToAltText = mediaAltText;
    }

    return postObject;
};
export const hasMentionInContent = (content: MetaData[]): boolean => {
    const mentionContent = content.filter((el) => _.includes(el.tags, MetaDataTag.Mention));

    return mentionContent.length > 0;
};
export const hasHashtagInContent = (content: MetaData[]): boolean => {
    const hashtagContent = content.filter((el) => _.includes(el.tags, MetaDataTag.HashTag));

    return hashtagContent.length > 0;
};
